import Box from "@mui/material/Box";
import { useTranslationApp } from "../../lib/i18next";
import {
  ADS_CAMPAIGN_CAROUSEL_FORMAT,
  ALERT_ICON_TYPE_ERROR,
  AUDIO_FORMATS,
  DOCUMENT_FORMATS_LINKEDIN,
  FILE_FORMATS,
  IMAGE_FORMATS,
  MAX_SIZE_DOCUMENT_LINKEDIN,
  MAX_SIZE_DOCUMENT_LINKEDIN_VALUE_DISPLAY,
  MAX_SIZE_FILES,
  MAX_SIZE_FILES_VALUE_DISPLAY,
  MAX_SIZE_IMAGE,
  MAX_SIZE_IMAGE_VALUE_DISPLAY,
  VIDEO_FORMATS,
} from "../../utils/constants";
import { SimpleAlert } from "../Alerts/Alerts";
import {
  BUTTON_CHANGE_CONTENT,
  BUTTON_UPLOAD_CONTENT,
  ERROR,
  FIELD_FILE_MAX_SIZE,
  FIELD_FILE_VALID_REQUIRED,
  FIELD_MEDIA_FILE_MIN_DIMENSIONS,
  GALLERY_UPLOAD_FOLDER_EMPTY,
} from "../../i18n/keysTranslations";
import { UploadFilesContext } from "../../contexts/UploadFilesContext";
import { useContext } from "react";
import { getDimensionsFromFileHtml } from "../../utils/string";
import {
  getFileExtension,
  isAcceptedUploadFile,
} from "../../utils/galleryDirectoryUpload";
import Button from "./Button";

const UploadFileButton = ({
  id = "upload-media",
  value,
  name,
  disabled = false,
  icon,
  onChange,
  onFileSelect = () => {},
  textUpload = BUTTON_UPLOAD_CONTENT,
  colorUpload = "primary",
  textRemove = BUTTON_CHANGE_CONTENT,
  colorRemove = "secondary",
  isHidden = false,
  disableVerificationMedia = false,

  acceptedFormats = [],
  multiple,
  modeUpload = "sync",
  minDimensions = {},

  pathRTDB,
  pathStorage,
  loading = false,
  onCallbackLoading = () => {},
  onUploadSettled = () => {},
  sx = {},
  type,
  directory = false,
}) => {
  const { t } = useTranslationApp();
  const { uploadFile } = useContext(UploadFilesContext) || {};

  const getPropsByValue = () => {
    if (value) {
      return {
        loading,
        color: colorUpload,
        variant: "contained",
      };
    }

    return {
      loading,
      color: colorRemove,
      variant: "contained",
    };
  };

  const props = getPropsByValue();

  const validateFiles = async (files) => {
    let hasFileNotAccepted = false;
    let hasFileToBig = false;
    let hasFileToSmall = false;
    let valueFileToBig = 0;

    for (const file of files) {
      const fileFormat = file.type.split("/")[1];
      const fileName = file.name.split(".")[1];

      if (
        !acceptedFormats.includes(fileName) &&
        !acceptedFormats.includes(fileFormat)
      ) {
        hasFileNotAccepted = true;
        break;
      }

      const isImage = IMAGE_FORMATS.includes(fileFormat);
      const fileExtension = (file.name.split(".").pop() || "").toLowerCase();
      const isDocument =
        DOCUMENT_FORMATS_LINKEDIN.includes(fileExtension) ||
        DOCUMENT_FORMATS_LINKEDIN.includes(fileFormat);
      const MAX_SIZE = isImage
        ? MAX_SIZE_IMAGE
        : isDocument
          ? MAX_SIZE_DOCUMENT_LINKEDIN
          : MAX_SIZE_FILES;
      const VALUE_DISPLAY = isImage
        ? MAX_SIZE_IMAGE_VALUE_DISPLAY
        : isDocument
          ? MAX_SIZE_DOCUMENT_LINKEDIN_VALUE_DISPLAY
          : MAX_SIZE_FILES_VALUE_DISPLAY;

      if (file.size > MAX_SIZE) {
        hasFileToBig = true;
        valueFileToBig = VALUE_DISPLAY;
        break;
      }

      if (
        (IMAGE_FORMATS.includes(fileFormat) ||
          VIDEO_FORMATS.includes(fileFormat)) &&
        minDimensions?.width &&
        minDimensions?.height
      ) {
        const urlBlob = URL.createObjectURL(file);

        const { width, height } = await getDimensionsFromFileHtml({
          urlBlob,
          file,
        });

        if (width < minDimensions?.width || height < minDimensions?.height) {
          hasFileToSmall = true;
          break;
        }
      }
    }

    if (hasFileNotAccepted) {
      SimpleAlert({
        title: t(ERROR),
        text: t(FIELD_FILE_VALID_REQUIRED, {
          value: acceptedFormats.join(", "),
        }),
        icon: ALERT_ICON_TYPE_ERROR,
      });
      return false;
    }
    if (hasFileToBig) {
      SimpleAlert({
        title: t(ERROR),
        text: t(FIELD_FILE_MAX_SIZE, {
          value: valueFileToBig,
          type: "MB",
        }),
        icon: ALERT_ICON_TYPE_ERROR,
      });
      return false;
    }

    if (hasFileToSmall) {
      SimpleAlert({
        title: t(ERROR),
        text: t(FIELD_MEDIA_FILE_MIN_DIMENSIONS, {
          width: minDimensions.width,
          height: minDimensions.height,
        }),
        icon: ALERT_ICON_TYPE_ERROR,
      });
      return false;
    }

    return true;
  };

  const getFileSizeLimit = (file) => {
    const mimeSubtype = String(file.type || "")
      .split("/")[1]
      ?.toLowerCase();
    const extension = getFileExtension(file.name);
    const isImage =
      IMAGE_FORMATS.includes(extension) || IMAGE_FORMATS.includes(mimeSubtype);
    const isDocument =
      DOCUMENT_FORMATS_LINKEDIN.includes(extension) ||
      DOCUMENT_FORMATS_LINKEDIN.includes(mimeSubtype);

    if (isImage) {
      return {
        maxSize: MAX_SIZE_IMAGE,
        valueDisplay: MAX_SIZE_IMAGE_VALUE_DISPLAY,
      };
    }

    if (isDocument) {
      return {
        maxSize: MAX_SIZE_DOCUMENT_LINKEDIN,
        valueDisplay: MAX_SIZE_DOCUMENT_LINKEDIN_VALUE_DISPLAY,
      };
    }

    return {
      maxSize: MAX_SIZE_FILES,
      valueDisplay: MAX_SIZE_FILES_VALUE_DISPLAY,
    };
  };

  const filterDirectoryFiles = async (files) => {
    const accepted = [];
    let hasUnsupported = false;
    let hasFileToBig = false;
    let hasFileToSmall = false;
    let valueFileToBig = 0;

    for (const file of files) {
      if (!isAcceptedUploadFile({ file, acceptedFormats })) {
        hasUnsupported = true;
        continue;
      }

      const { maxSize, valueDisplay } = getFileSizeLimit(file);
      if (file.size > maxSize) {
        hasFileToBig = true;
        valueFileToBig = valueDisplay;
        continue;
      }

      const mimeSubtype = String(file.type || "")
        .split("/")[1]
        ?.toLowerCase();
      const isVisual =
        IMAGE_FORMATS.includes(mimeSubtype) ||
        VIDEO_FORMATS.includes(mimeSubtype);

      if (isVisual && minDimensions?.width && minDimensions?.height) {
        const urlBlob = URL.createObjectURL(file);
        const { width, height } = await getDimensionsFromFileHtml({
          urlBlob,
          file,
        });

        if (width < minDimensions.width || height < minDimensions.height) {
          hasFileToSmall = true;
          continue;
        }
      }

      accepted.push(file);
    }

    return {
      accepted,
      hasUnsupported,
      hasFileToBig,
      hasFileToSmall,
      valueFileToBig,
    };
  };

  const alertDirectoryRejected = ({
    hasUnsupported,
    hasFileToBig,
    hasFileToSmall,
    valueFileToBig,
  }) => {
    if (!hasUnsupported && hasFileToBig && !hasFileToSmall) {
      SimpleAlert({
        title: t(ERROR),
        text: t(FIELD_FILE_MAX_SIZE, {
          value: valueFileToBig,
          type: "MB",
        }),
        icon: ALERT_ICON_TYPE_ERROR,
      });
      return;
    }

    if (!hasUnsupported && hasFileToSmall && !hasFileToBig) {
      SimpleAlert({
        title: t(ERROR),
        text: t(FIELD_MEDIA_FILE_MIN_DIMENSIONS, {
          width: minDimensions.width,
          height: minDimensions.height,
        }),
        icon: ALERT_ICON_TYPE_ERROR,
      });
      return;
    }

    SimpleAlert({
      title: t(ERROR),
      text: t(GALLERY_UPLOAD_FOLDER_EMPTY, {
        value: acceptedFormats
          .filter((format) => format !== ADS_CAMPAIGN_CAROUSEL_FORMAT)
          .join(", "),
      }),
      icon: ALERT_ICON_TYPE_ERROR,
    });
  };

  const onChangeFile = async (e) => {
    const files = e.target.files;

    if (files.length === 0) return;

    try {
      let filesToUpload = files;

      if (directory) {
        onCallbackLoading(true);
        const filtered = await filterDirectoryFiles(Array.from(files));
        if (filtered.accepted.length === 0) {
          alertDirectoryRejected(filtered);
          onCallbackLoading(false);
          return;
        }
        filesToUpload = filtered.accepted;
      }

      if (onFileSelect) {
        const shouldContinue = await onFileSelect(filesToUpload);
        if (shouldContinue === false) {
          if (directory) onCallbackLoading(false);
          return;
        }
      }

      if (!directory) {
        const isValidFiles = await validateFiles(filesToUpload);
        if (!isValidFiles) {
          return;
        }
      }

      let arrayUrls = [];
      if (modeUpload === "async") {
        for (const file of filesToUpload) {
          let preview = URL.createObjectURL(file);

          preview = `${preview}?ext=${file.type.split("/")[1]}`;

          arrayUrls.push(preview);
        }
        onChange(arrayUrls);
      }

      const reportLoading = (loading) => {
        if (!loading && directory) return;
        onCallbackLoading(loading);
      };

      const uploadedUrls = [];
      let index = 0;
      for (const file of filesToUpload) {
        const urlUploaded =
          modeUpload === "sync"
            ? await uploadFile({
                file,
                pathStorage,
                pathRTDB,
                onCallbackLoading: reportLoading,
                doCompression: true,
                modeUpload,
                urlPreview: arrayUrls[index],
                disableVerificationMedia,
              })
            : uploadFile({
                file,
                pathStorage,
                pathRTDB,
                onCallbackLoading: reportLoading,
                doCompression: true,
                modeUpload,
                urlPreview: arrayUrls[index],
                disableVerificationMedia,
              });

        index++;

        if (modeUpload === "sync" && urlUploaded) {
          uploadedUrls.push(urlUploaded);
        }
      }

      if (modeUpload === "sync" && uploadedUrls.length > 0) {
        await onChange(uploadedUrls);
      }
    } finally {
      const input = document.getElementById(id);
      if (input?.value) {
        input.value = "";
      }
      if (directory) onCallbackLoading(false);
      onUploadSettled();
    }
    return true;
  };

  if (modeUpload === "async" && !pathRTDB)
    throw new Error("When modeUpload is async, pathRTDB is required");

  const acceptValue = acceptedFormats
    .map((item) => {
      if (IMAGE_FORMATS.includes(item)) return `image/${item},`;
      if (AUDIO_FORMATS.includes(item)) return `audio/${item},`;
      if (
        FILE_FORMATS.includes(item) ||
        DOCUMENT_FORMATS_LINKEDIN.includes(item)
      )
        return `.${item},`;
      return `video/${item},`;
    })
    .join(" ");

  const fileInput = (
    <input
      id={id}
      multiple={multiple || directory}
      accept={directory ? undefined : acceptValue}
      name={name}
      type="file"
      hidden
      onChange={onChangeFile}
      {...(directory
        ? {
            webkitdirectory: "",
            directory: "",
            mozdirectory: "",
          }
        : {})}
    />
  );

  if (type === "icon") {
    return (
      <Box
        sx={{
          display: isHidden ? "none" : "flex",
        }}
      >
        <Button
          {...props}
          type="button"
          component="label"
          disabled={disabled}
          sx={{
            ...sx,
          }}
        >
          {icon && !loading && icon}
          {fileInput}
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: isHidden ? "none" : "flex",
      }}
    >
      <Button
        {...props}
        type="button"
        component={"label"}
        disabled={disabled}
        endIcon={icon && !loading && icon}
        sx={sx}
      >
        {t(value ? textRemove : textUpload)}
        {fileInput}
      </Button>
    </Box>
  );
};

export default UploadFileButton;
